import { useEffect } from 'react';
import { create } from 'zustand';

import { useAppConfig } from '#/config/AppConfigContext';
import type { AppConfig } from '#/config/types/AppConfig';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';

const loadCssBundleStore = create<{
  bundles: Record<string, 'loaded' | 'errored'>;
}>(() => ({ bundles: {} }));

const useLoadCssBundleStore = loadCssBundleStore; // The store can be used as a hook

const bundlesLoading: string[] = [];

// Actions

function setCssBundleStatus(name: string, status: 'loaded' | 'errored') {
  const { bundles } = loadCssBundleStore.getState();
  loadCssBundleStore.setState({ bundles: { ...bundles, [name]: status } });
}

function loadCssBundle(name: string, appConfig: AppConfig) {
  const styleEl = document.createElement('link');
  styleEl.href = `${appConfig.idealist.staticJsPath}../${name}.css`;
  styleEl.type = 'text/css';
  styleEl.rel = 'stylesheet';
  styleEl.onload = () => setCssBundleStatus(name, 'loaded');
  styleEl.onerror = () => setCssBundleStatus(name, 'errored');

  document.head.appendChild(styleEl);
}

// Hooks

// This store can be removed after migrating to horizon
export function useLoadBundleCssOnce(name: string): {
  cssLoadingStatus: 'loading' | 'loaded' | 'errored';
} {
  const { appConfig } = useAppConfig();
  const storeStatus = useLoadCssBundleStore((state) =>
    name ? state.bundles[name] : undefined,
  );

  useEffect(() => {
    if (HORIZON_ENABLED) return;

    if (typeof storeStatus === 'undefined' && !bundlesLoading.includes(name)) {
      bundlesLoading.push(name);
      loadCssBundle(name, appConfig);
    }
  }, [name, storeStatus, appConfig]);

  if (HORIZON_ENABLED) return { cssLoadingStatus: 'loaded' };

  return { cssLoadingStatus: storeStatus ?? 'loading' };
}
